import React, { useState, useEffect } from 'react';
import {
  AlertCircle,
  Bot,
  CheckCircle,
  Info,
  RefreshCw,
  Search,
  Undo2,
} from 'lucide-react';
import { userService } from '../../services/database/userService';
import aiUsageService from '../../services/ai/AIUsageService';
import { AIUsageSettings, AIUserLimitInfo } from '../../types/ai';
import type { User } from '../../types/auth';

// Trang Cài đặt hệ thống. Chức năng đang hoạt động duy nhất là GIỚI HẠN AI.
// (Đã bỏ "Kết nối Backend WordPress" + khung system-settings/backup cũ — không còn dùng.)
const Settings: React.FC = () => {
  const [aiSettings, setAiSettings] = useState<AIUsageSettings | null>(null);
  const [aiSettingsLoading, setAiSettingsLoading] = useState(true);
  const [aiSettingsError, setAiSettingsError] = useState<string | null>(null);
  const [aiDailyLimitInput, setAiDailyLimitInput] = useState(5);
  const [isSavingAi, setIsSavingAi] = useState(false);
  const [aiSaveStatus, setAiSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const [userLimitSearch, setUserLimitSearch] = useState('');
  const [userLookupLoading, setUserLookupLoading] = useState(false);
  const [userLookupError, setUserLookupError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userLimitInfo, setUserLimitInfo] = useState<AIUserLimitInfo | null>(null);
  const [userLimitInput, setUserLimitInput] = useState(5);
  const [userLimitNotes, setUserLimitNotes] = useState('');
  const [userLimitSaving, setUserLimitSaving] = useState(false);
  const [userLimitStatus, setUserLimitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    loadAiSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAiSettings = async () => {
    try {
      setAiSettingsLoading(true);
      const data = await aiUsageService.getSettings();
      setAiSettings(data);
      setAiDailyLimitInput(data.dailyLimit);
      setAiSettingsError(null);
    } catch (error: any) {
      setAiSettingsError(error?.message || 'Không thể tải cấu hình giới hạn AI.');
    } finally {
      setAiSettingsLoading(false);
    }
  };

  const handleAiLimitSave = async () => {
    try {
      setIsSavingAi(true);
      setAiSaveStatus('idle');
      const data = await aiUsageService.updateSettings(aiDailyLimitInput);
      setAiSettings(data);
      setAiDailyLimitInput(data.dailyLimit);
      setAiSaveStatus('success');
      setTimeout(() => setAiSaveStatus('idle'), 3000);
    } catch (error: any) {
      setAiSaveStatus('error');
      setAiSettingsError(error?.message || 'Không thể cập nhật giới hạn AI.');
    } finally {
      setIsSavingAi(false);
    }
  };

  const lookupUserLimit = async () => {
    const keyword = userLimitSearch.trim();
    if (!keyword) {
      setUserLookupError('Nhập email của học sinh cần tìm.');
      return;
    }

    try {
      setUserLookupLoading(true);
      setUserLookupError(null);
      setSelectedUser(null);
      setUserLimitInfo(null);

      const user =
        (await userService.getUserByEmail(keyword)) ||
        (await userService.getUserById(keyword));

      if (!user) {
        setUserLookupError('Không tìm thấy học sinh với email hoặc ID này.');
        return;
      }

      setSelectedUser(user);
      const info = await aiUsageService.getUserLimit(user.id);
      setUserLimitInfo(info);
      setUserLimitInput(info.limitType === 'custom' ? info.dailyLimit : info.globalLimit);
      setUserLimitNotes(info.notes || '');
    } catch (error: any) {
      setUserLookupError(error?.message || 'Không thể tải thông tin giới hạn của học sinh.');
    } finally {
      setUserLookupLoading(false);
    }
  };

  const handleSaveUserLimit = async () => {
    if (!selectedUser) return;
    try {
      setUserLimitSaving(true);
      setUserLimitStatus('idle');
      const info = await aiUsageService.setUserLimit(
        selectedUser.id,
        userLimitInput,
        userLimitNotes || undefined
      );
      setUserLimitInfo(info);
      setUserLimitStatus('success');
      setTimeout(() => setUserLimitStatus('idle'), 2500);
    } catch (error: any) {
      setUserLimitStatus('error');
      setUserLookupError(error?.message || 'Không thể cập nhật giới hạn cá nhân.');
    } finally {
      setUserLimitSaving(false);
    }
  };

  const handleResetUserLimit = async () => {
    if (!selectedUser) return;
    try {
      setUserLimitSaving(true);
      setUserLimitStatus('idle');
      const info = await aiUsageService.deleteUserLimit(selectedUser.id);
      setUserLimitInfo(info);
      setUserLimitInput(info.globalLimit);
      setUserLimitNotes('');
      setUserLimitStatus('success');
      setTimeout(() => setUserLimitStatus('idle'), 2500);
    } catch (error: any) {
      setUserLimitStatus('error');
      setUserLookupError(error?.message || 'Không thể khôi phục giới hạn mặc định.');
    } finally {
      setUserLimitSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Cài đặt hệ thống</h1>
        <p className="text-gray-600 mt-2">Cấu hình giới hạn sử dụng AI cho học sinh</p>
      </div>

      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center space-x-2 mb-6">
          <Bot className="w-6 h-6 text-purple-600" />
          <h2 className="text-xl font-semibold text-gray-900">Giới hạn AI</h2>
        </div>

        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start space-x-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-900">
              <p className="font-medium mb-1">Giới hạn lượt hỏi AI theo ngày</p>
              <p>Thiết lập số lượt hỏi/giải thích AI tối đa cho mỗi học sinh trong 24 giờ để tránh lạm dụng. (Áp dụng cho phần hỏi–đáp AI; không liên quan giới hạn tài khoản dùng thử 3 ngày của soạn bài/PMaker.)</p>
            </div>
          </div>

          {aiSettingsError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start space-x-2">
              <AlertCircle className="w-4 h-4 mt-0.5" />
              <span>{aiSettingsError}</span>
            </div>
          )}

          {aiSettingsLoading ? (
            <div className="flex items-center space-x-2 text-gray-600">
              <RefreshCw className="w-4 h-4 animate-spin" />
              <span>Đang tải cấu hình AI...</span>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Giới hạn lượt hỏi AI mỗi ngày
                </label>
                <input
                  type="number"
                  min={1}
                  max={aiSettings?.maxLimit || 50}
                  value={aiDailyLimitInput}
                  onChange={(e) => {
                    const raw = parseInt(e.target.value, 10);
                    if (Number.isNaN(raw)) {
                      setAiDailyLimitInput(1);
                      return;
                    }
                    const maxLimit = aiSettings?.maxLimit || 50;
                    const clamped = Math.min(Math.max(raw, 1), maxLimit);
                    setAiDailyLimitInput(clamped);
                  }}
                  className="w-32 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-center font-semibold text-lg"
                />
                <p className="mt-2 text-sm text-gray-600">
                  Hiện tại: <strong>{aiSettings?.dailyLimit}</strong> lượt/ngày · Tối đa {aiSettings?.maxLimit || 50} lượt/ngày
                </p>
              </div>

              <div className="flex items-center space-x-3">
                {aiSaveStatus === 'success' && (
                  <span className="inline-flex items-center text-green-600 text-sm">
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Đã cập nhật giới hạn
                  </span>
                )}
                {aiSaveStatus === 'error' && (
                  <span className="inline-flex items-center text-red-600 text-sm">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    Cập nhật thất bại
                  </span>
                )}

                <button
                  onClick={handleAiLimitSave}
                  disabled={isSavingAi}
                  className="px-5 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center space-x-2"
                >
                  {isSavingAi ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Bot className="w-4 h-4" />
                  )}
                  <span>{isSavingAi ? 'Đang cập nhật...' : 'Cập nhật giới hạn'}</span>
                </button>
              </div>
            </>
          )}

          <div className="border-t pt-6">
            <h4 className="text-base font-semibold text-gray-900 mb-2">Giới hạn theo từng học sinh</h4>
            <p className="text-sm text-gray-600 mb-4">
              Cho phép đặt số lượt riêng cho các học sinh đặc biệt (ví dụ gói Pro). Nếu không đặt, hệ thống dùng giới hạn chung ở trên.
            </p>

            <div className="flex flex-col md:flex-row md:items-end gap-3 mb-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email hoặc User ID
                </label>
                <input
                  type="text"
                  value={userLimitSearch}
                  onChange={(e) => setUserLimitSearch(e.target.value)}
                  placeholder="ví dụ: student@example.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <button
                onClick={lookupUserLimit}
                disabled={userLookupLoading || !userLimitSearch.trim()}
                className="flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 h-11"
              >
                {userLookupLoading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Search className="w-4 h-4" />
                )}
                <span>Tìm học sinh</span>
              </button>
            </div>

            {userLookupError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start space-x-2">
                <AlertCircle className="w-4 h-4 mt-0.5" />
                <span>{userLookupError}</span>
              </div>
            )}

            {selectedUser && userLimitInfo && (
              <div className="border rounded-lg p-4 space-y-4 bg-gray-50">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div>
                    <p className="text-sm text-gray-500">Học sinh</p>
                    <p className="font-semibold text-gray-900">
                      {selectedUser.name || selectedUser.email}
                    </p>
                    <p className="text-sm text-gray-600">{selectedUser.email}</p>
                  </div>
                  <div className="text-sm">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${userLimitInfo.limitType === 'custom'
                        ? 'bg-purple-100 text-purple-700'
                        : 'bg-gray-100 text-gray-700'
                        }`}
                    >
                      {userLimitInfo.limitType === 'custom' ? 'Custom' : 'Mặc định'}
                    </span>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Giới hạn riêng (lượt/ngày)
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={aiSettings?.maxLimit || 50}
                      value={userLimitInput}
                      onChange={(e) => setUserLimitInput(Math.max(1, Math.min(Number(e.target.value), aiSettings?.maxLimit || 50)))}
                      className="w-32 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Giới hạn chung hiện tại: {userLimitInfo.globalLimit} lượt/ngày
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ghi chú
                    </label>
                    <input
                      type="text"
                      value={userLimitNotes}
                      onChange={(e) => setUserLimitNotes(e.target.value)}
                      placeholder="Ví dụ: Học sinh Pro, lớp nâng cao..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  {userLimitStatus === 'success' && (
                    <span className="inline-flex items-center text-green-600 text-sm">
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Đã lưu thay đổi
                    </span>
                  )}
                  {userLimitStatus === 'error' && (
                    <span className="inline-flex items-center text-red-600 text-sm">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      Lưu thất bại
                    </span>
                  )}

                  <div className="flex items-center space-x-3 ml-auto">
                    <button
                      onClick={handleResetUserLimit}
                      disabled={userLimitInfo.limitType !== 'custom' || userLimitSaving}
                      className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                      <Undo2 className="w-4 h-4" />
                      <span>Khôi phục mặc định</span>
                    </button>

                    <button
                      onClick={handleSaveUserLimit}
                      disabled={userLimitSaving}
                      className="flex items-center space-x-2 px-5 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                    >
                      {userLimitSaving ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Bot className="w-4 h-4" />
                      )}
                      <span>Lưu giới hạn riêng</span>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
